import { useCallback, useMemo, useState } from "react";

import { BdsSelectItem, type BdsSelectProps } from "@buildertrend/components";

import { BTSelectItem } from "types/apiResponse/apiResponse";

import { isNullOrWhitespace } from "utilities/string/string";

import { type BTSelectProps } from "commonComponents/btWrappers/BTSelect/BTSelect";

import "./BTSelectCreatable.less";

export const creatableSelectOption: string = "-9999999999";

interface ICommonCreatableProps {
    onOptionCreate: (field: string, value?: string | number, search?: string) => void;
    maxLength: number;
    createableEntityName?: string;
    canCreateEntity: boolean;
}

type BTCreatableInput<ExtraDataType> = ICommonCreatableProps &
    Pick<BTSelectProps<unknown, ExtraDataType>, "onChange" | "treeData"> & {
        useBds?: false;
    };

type BdsCreatableInput<ExtraDataType> = ICommonCreatableProps &
    Pick<BdsSelectProps<unknown, ExtraDataType>, "onChange" | "treeData"> & {
        useBds: true;
    };

type UseSelectCreatableResult<ExtraDataType> =
    | Pick<
          BTSelectProps<unknown, ExtraDataType>,
          "onChange" | "onSearch" | "notFoundContent" | "treeData"
      >
    | Pick<
          BdsSelectProps<unknown, ExtraDataType>,
          "onChange" | "onSearch" | "notFoundContent" | "treeData"
      >;

interface ISelectItemLike {
    title?: string;
    children?: readonly ISelectItemLike[];
}

export function useSelectCreatable<ExtraDataType>(
    props: BTCreatableInput<ExtraDataType> | BdsCreatableInput<ExtraDataType>,
): UseSelectCreatableResult<ExtraDataType> {
    const {
        onChange,
        treeData: originalTreeData,
        onOptionCreate,
        maxLength,
        createableEntityName,
        canCreateEntity,
        useBds,
    } = props;

    const [search, setSearch] = useState<string>();

    const maxExceeded = (search?.length ?? 0) > maxLength;

    const treeData = useMemo(() => {
        if (!originalTreeData || !search || maxExceeded) {
            return originalTreeData;
        }
        const createOptionData = {
            id: creatableSelectOption,
            title: createableEntityName
                ? `Create ${createableEntityName} "${search}"`
                : `Create "${search}"`,
            className: "SelectCreatableOption",
        };
        const createOption = useBds
            ? new BdsSelectItem<ExtraDataType>(createOptionData)
            : new BTSelectItem<ExtraDataType>(createOptionData);
        return [...(originalTreeData as readonly unknown[]), createOption];
    }, [maxExceeded, originalTreeData, search, createableEntityName, useBds]);

    const handleChange = useCallback(
        (
            field: string,
            value?: string | number,
            selectedItem?: BTSelectItem<ExtraDataType> | BdsSelectItem<ExtraDataType>,
        ) => {
            if (value?.toString() === creatableSelectOption) {
                onOptionCreate(field, value, search);
            } else {
                (
                    onChange as (
                        field: string,
                        value: unknown,
                        selectedItem?: BTSelectItem<ExtraDataType> | BdsSelectItem<ExtraDataType>,
                    ) => void
                )(field, value, selectedItem);
            }
            setSearch(undefined);
        },
        [onChange, onOptionCreate, search],
    );

    const handleChildSearch = useCallback((node: ISelectItemLike, targetTitle: string): boolean => {
        if (node.title && node.title.toLocaleLowerCase() === targetTitle.toLocaleLowerCase()) {
            return true;
        }

        if (node.children && node.children.length > 0) {
            for (const child of node.children) {
                if (handleChildSearch(child, targetTitle)) {
                    return true;
                }
            }
        }
        return false;
    }, []);

    const handleSearch = useCallback(
        (value: string) => {
            const removeCreate =
                isNullOrWhitespace(value) ||
                (originalTreeData as readonly ISelectItemLike[] | undefined)?.find((item) =>
                    handleChildSearch(item, value),
                );
            setSearch(removeCreate ? undefined : value);
        },
        [originalTreeData, handleChildSearch],
    );

    if (!canCreateEntity) {
        return { treeData: originalTreeData } as UseSelectCreatableResult<ExtraDataType>;
    }

    const notFoundContent =
        search && maxExceeded ? `${search.length - maxLength} characters over` : undefined;

    return {
        onChange: handleChange,
        onSearch: handleSearch,
        notFoundContent,
        treeData,
    } as UseSelectCreatableResult<ExtraDataType>;
}
